/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetAttributeError';

const ActionSetAttributeErrorManager: ActionManagerFn<
  'ActionSetAttributeError'
> = (platform: Platform): ActionManager<'ActionSetAttributeError'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionSetAttributeError'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      targetAttribute: {
        label: 'Target Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
        attributeRefreshCallback: (cb) => {
          callback = cb;
        },
      },
      messageExpression: {
        label: 'Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Leave blank or set null to clear an existing error message',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'alias') {
        callback();
        return {
          [property]: value,
          targetAttribute: defaultValue.targetAttribute,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'targetAttribute':
          return !!props.alias;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    name: 'Set Attribute Error',
    icon: { iconName: 'bug' },
    description: `**Overview**<br/>
The Set Attribute Error action is a validation control component designed to programmatically mark a specific field within a Data Store as invalid. By associating a custom error message with a specific attribute, this action forces any bound UI components (like Text Inputs or Dropdowns) to enter their "Error" state, typically displaying a red outline and the provided message text.<br/><br/>
This component is essential for implementing custom validation logic that goes beyond standard required/format checks. It operates directly on the Data Store, meaning the record itself is flagged as invalid, which can prevent it from being saved until the error is resolved. It also serves as the mechanism to clear errors when validity is restored.

**Key Features**<br/>
* **Targeted Invalidation:** Pinpoints a specific attribute (column) within a Data Store record to flag, ensuring that feedback is displayed exactly where the user needs to correct input.
* **Dynamic Feedback:** Uses an expression for the messageExpression, allowing developers to construct context-aware error messages (e.g., "Age must be greater than 18" vs "Age cannot be negative").
* **Toggle Behavior:** Acts as both a "Set Error" and "Clear Error" function. Passing a non-empty string sets the error; passing an empty string or null removes the error state, restoring the field to valid.
* **Broad Store Compatibility:** Works across CloudDataStore, LocalDataStore, and TransientDataStore, making it useful for both persistent database records and temporary UI forms.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the Data Store containing the record to validate.
* **Target Attribute (Required):** A ViewAttributeChooser property.
  * *Function:* The specific field/column to mark as invalid.
* **Message:** An expression property returning a string.
  * *Function:* The text to display to the user.
  * *Logic:* If valid text is provided, the field is marked as error. If blank or null, the error is cleared.

**Use Cases**<br/>
* **Cross-Field Validation:** Checking if "End Date" is before "Start Date" and using this action to set an error on the "End Date" field if true.
* **Async Validation:** marking an "Email" field as invalid with the message "Email already taken" after a server-side check (ActionInvokeCloudFunction) returns a duplicate status.
* **Complex Business Logic:** Enforcing rules that are too complex for standard regex, such as "Loan Amount cannot exceed 80% of Collateral Value."`,
  };
};

export default ActionSetAttributeErrorManager;
